import LineFunctionResult from './LineFunctionResult.js';
import {nicelyRounded} from '../../util/CommonUtils.js';

export default class LineFunctionRegression {
	constructor(labelPattern, params) {
		this._labelPattern = labelPattern;
		if (params.client == null)
			throw new Error('RegressionApiClient is required');
		this._client = params.client;
	}

	async calculateLine(x, y, xAxis, yAxis) {
		const data = PolynomialRegressionDataSet.create(x, y);
		const {polynomial, r2} = await this._client.getPolynomialRegression(data.createRequest());
		const points = data instanceof LinearDataSet ? this._calcLinear(polynomial, xAxis, yAxis) : this._calcOrdinal(polynomial, data.size());
		const label = this._labelPattern.replace('${polynomial}', polynomial.toString('x', nicelyRounded)).replace('${r2}', nicelyRounded(r2))
		return [new LineFunctionResult(points, label)];
	}

	_calcLinear(polynomial, xAxis, yAxis) {
		let px0 = Math.min(Math.max(polynomial.solve(yAxis[0]), xAxis[0]), xAxis[xAxis.length - 1]);
		let px1 = Math.min(Math.max(polynomial.solve(yAxis[1]), xAxis[0]), xAxis[xAxis.length - 1]);
		let py0 = polynomial.eval(px0);
		let py1 = polynomial.eval(px1);
		return [[px0, py0], [px1, py1]].sort((a, b) => a[0] - b[0]);
	}

	_calcOrdinal(polynomial, length) {
		return [[-Infinity, polynomial.eval(-.5)], [Infinity, polynomial.eval(length - 0.5)]];
	}
}

class PolynomialRegressionDataSet {

	constructor(x, y) {
		this.points = this._createPoints(x, y);
		this.points.sort((a, b) => a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0)
	}

	createRequest() {
		throw new Error('Not implemented');
	}

	getDomainValue(i) {
		throw new Error('Not implemented');
	}

	size() {
		return this.points.length;
	}

	_createPoints(x, y) {
		throw new Error('Not implemented');
	}

	static create(x, y) {
		if (x.every(el=> Number.isFinite(el)))
			return new LinearDataSet(x, y)
		return new OrdinalDataSet(x, y);
	}
}

class LinearDataSet extends PolynomialRegressionDataSet {
	constructor(x, y) {
		super(x, y);
	}

	createRequest() {
		const x = [], y = [];
		for (const point of this.points) {
			x.push(point[0]);
			y.push(point[1]);
		}
		if (this.points.length === 1){
			x.push(x[0]);
			y.push(y[1]);
		}
		return {x: x, y: y}
	}

	getDomainValue(i) {
		let val = this.points[i][0];
		return {
			natural: val,
			abstract: val
		};
	}

	_createPoints(x, y) {
		const points = [];
		for (let i = 0; i < x.length; i++)
			points.push([x[i], y[i]])
		return points;
	}
}

class OrdinalDataSet extends PolynomialRegressionDataSet {
	constructor(x, y) {
		super(x, y);
	}

	createRequest() {
		const x = [], y = [];
		for (const point of this.points) {
			x.push(point[0]);
			y.push(point[1]);
		}
		return {x: x, y: y}
	}

	getDomainValue(i) {
		let val = this.points[i][0];
		return {
			natural: this.uniqueDomain[val],
			abstract: val
		};
	}

	_createPoints(x, y) {
		this.uniqueDomain = [...new Set(x).values()].sort((a, b) => a < b ? -1 : a > b ? 1 : 0)
		const points = [];
		for (let i = 0; i < x.length; i++)
			points.push([this.uniqueDomain.indexOf(x[i]), y[i]])
		return points;
	}
}